import clsx from 'clsx';
import { House, LogOut, Menu, Search, Settings2 } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAparencia } from '../lib/aparencia';
import { useSessao } from '../lib/sessao';
import { MenuUsuario } from './menu-usuario';
import { PaletaComandos } from './paleta-comandos';

/** Botão de ícone da barra — mesma medida e mesmo realce para todos. */
const BOTAO_ICONE =
  'flex size-9 items-center justify-center rounded-lg text-tinta-700 transition-colors hover:bg-marca-100 hover:text-marca-600';

/**
 * Barra do topo.
 *
 * Carrega o que precisa estar à mão em QUALQUER tela:
 *
 *  - o botão de menu: no celular abre a gaveta, no desktop esconde a lateral
 *  - o atalho de início
 *  - a busca rápida (Ctrl/⌘ K), que acha tela pelo rótulo
 *  - aparência, o menu do usuário e a saída
 *
 * O Adonai carrega aqui também o estado de uma fila de envio offline
 * (`useFila`) que este produto não tem — cadastro societário não roda no chão
 * de fábrica sem internet. Removido, não adaptado.
 *
 * O título da página NÃO vem aqui: cada página cuida do próprio título.
 * Duplicar o título em dois lugares é ruído.
 */
export function Topbar({
  aoAbrirMenu,
  aoAbrirAparencia,
  paletaAberta,
  aoAbrirPaleta,
  aoFecharPaleta,
}: {
  aoAbrirMenu: () => void;
  aoAbrirAparencia: () => void;
  paletaAberta: boolean;
  aoAbrirPaleta: () => void;
  aoFecharPaleta: () => void;
}) {
  const alternarOculta = useAparencia((a) => a.alternarOculta);
  const sair = useSessao((s) => s.sair);
  const navegar = useNavigate();

  // Ctrl/⌘ K de qualquer lugar. Fica aqui, e não na paleta, porque a paleta
  // só existe no DOM quando está aberta — o atalho que a ABRE não pode
  // depender dela estar montada.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (paletaAberta) aoFecharPaleta();
        else aoAbrirPaleta();
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [paletaAberta, aoAbrirPaleta, aoFecharPaleta]);

  const aoSair = async () => {
    await sair();
    navegar('/entrar');
  };

  return (
    <>
      <header className="border-tinta-100 bg-superficie z-30 flex h-16 shrink-0 items-center gap-1.5 border-b px-3 sm:px-4">
        {/* No celular abre a gaveta; no desktop esconde a lateral por inteiro. */}
        <button
          type="button"
          onClick={aoAbrirMenu}
          className={clsx(BOTAO_ICONE, 'lg:hidden')}
          aria-label="Abrir menu"
        >
          <Menu className="size-5" />
        </button>
        <button
          type="button"
          onClick={alternarOculta}
          className={clsx(BOTAO_ICONE, 'hidden lg:flex')}
          aria-label="Mostrar ou esconder o menu"
          title="Mostrar/esconder menu"
        >
          <Menu className="size-5" />
        </button>

        <Link to="/" className={BOTAO_ICONE} aria-label="Início" title="Início">
          <House className="size-5" />
        </Link>

        {/* Busca rápida: acha tela pelo rótulo. */}
        <div className="flex flex-1 justify-center px-1 sm:px-3">
          <button
            type="button"
            onClick={aoAbrirPaleta}
            aria-label="Buscar (Ctrl K)"
            className="border-tinta-100 bg-superficie-2 text-tinta-500 hover:border-marca-500 flex h-9 w-full max-w-md items-center gap-2 rounded-lg border px-3 text-sm transition-colors"
          >
            <Search className="size-4 shrink-0" aria-hidden />
            <span className="flex-1 truncate text-left">Buscar tela…</span>
            <kbd className="border-tinta-100 hidden rounded border px-1.5 py-0.5 text-[10px] font-medium sm:inline">
              Ctrl K
            </kbd>
          </button>
        </div>

        <button
          type="button"
          onClick={aoAbrirAparencia}
          title="Aparência"
          aria-label="Aparência"
          className={BOTAO_ICONE}
        >
          <Settings2 className="size-4" />
        </button>

        <MenuUsuario />

        {/*
          "Sair" direto na barra — peça NOSSA, não portada do Adonai (lá ele
          só mora dentro do menu suspenso). Existe porque o `Dropdown` só
          monta o conteúdo no DOM quando está aberto, e o cenário de e2e
          (`getByRole('button', { name: 'Sair' })`) clica sem abrir menu
          nenhum. É o ÚNICO "Sair" do produto: não repete dentro do
          `MenuUsuario` — duplicar seria redundância sem ganho, já que este
          botão nunca some.
        */}
        <button
          type="button"
          onClick={() => void aoSair()}
          title="Sair"
          className="text-tinta-700 hover:bg-erro-100 hover:text-erro-600 flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm transition-colors"
        >
          <LogOut className="size-4" aria-hidden />
          Sair
        </button>
      </header>

      <PaletaComandos aberta={paletaAberta} aoFechar={aoFecharPaleta} />
    </>
  );
}
